/**
 * Analytics event names for the team (workspace membership) domain.
 *
 * Names follow the repo convention `front.<domain>.<entity>.<action>` in
 * snake_case. The canonical event catalog documents `member.added` as the
 * logical name for `MEMBER_ADDED`.
 */
export const TEAM_EVENTS = {
  /**
   * Emitted once per confirmed workspace member addition, after the backend
   * accepts it — never on the click, so an attempt that the backend rejects
   * cannot be read as a member joining. Carries the surface it was added from
   * and the channel it travelled through, so the settings path and the
   * onboarding path can be split without joining to another source.
   */
  MEMBER_ADDED: 'front.team.member.added',
} as const;

/**
 * Union of every valid team-domain analytics event name.
 */
export type tTeamEventName = (typeof TEAM_EVENTS)[keyof typeof TEAM_EVENTS];

/**
 * Entry points a workspace member can be added from, carried as the `surface`
 * property of `MEMBER_ADDED`.
 *
 * `settings` and `onboarding` have emitters today. `post_connection` belongs to
 * the invite step shown right after a channel connects, which is scheduled but
 * not built; it is declared here because the value space is fixed now.
 */
export const TEAM_MEMBER_ADDED_SURFACES = [
  'settings',
  'onboarding',
  'post_connection',
] as const;

/**
 * Union of every valid `surface` value of `MEMBER_ADDED`.
 */
export type tTeamMemberAddedSurface =
  (typeof TEAM_MEMBER_ADDED_SURFACES)[number];

/**
 * Ways the member reaches the workspace, carried as the `channel` property of
 * `MEMBER_ADDED`. `direct_create` is the legacy path that creates the account
 * outright; `invitation` sends a link the member has to accept.
 *
 * Only those two have emitters today; `whatsapp` belongs to the invite-by-
 * WhatsApp flow, which is scheduled but not built.
 */
export const TEAM_MEMBER_ADDED_CHANNELS = [
  'direct_create',
  'invitation',
  'whatsapp',
] as const;

/**
 * Union of every valid `channel` value of `MEMBER_ADDED`.
 */
export type tTeamMemberAddedChannel =
  (typeof TEAM_MEMBER_ADDED_CHANNELS)[number];
